"use client"

import { useEffect, useRef, useState } from "react"

import { CUSTOM_BUILD_SUBJECT } from "@/lib/contact-subjects"
import { trackEvent } from "@/lib/analytics"

const MIN_MESSAGE_LENGTH = 10
const MAX_MESSAGE_LENGTH = 1000

/** Mirror of the API's sanitizeInput so client length checks match what the
 * server actually validates after stripping risky characters. */
function sanitizedLength(input: string): number {
  return input
    .trim()
    .replace(/[<>]/g, "")
    .replace(/javascript:/gi, "")
    .replace(/on\w+=/gi, "").length
}

type SubmitState = "idle" | "submitting" | "done"

export function StudioInquiryForm() {
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [message, setMessage] = useState("")
  const [state, setState] = useState<SubmitState>("idle")
  const [ticketNumber, setTicketNumber] = useState("")
  const [error, setError] = useState("")
  const successRef = useRef<HTMLDivElement>(null)

  // The success card replaces the focused submit button; move keyboard focus
  // onto it so focus is not silently dropped to <body>.
  useEffect(() => {
    if (state === "done") successRef.current?.focus()
  }, [state])

  const validate = (): string => {
    if (sanitizedLength(name) < 2) return "Add your name so we know who to reply to."
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
      return "That email doesn't look right. Check it and try again."
    if (sanitizedLength(message) < MIN_MESSAGE_LENGTH)
      return "Tell us a little more about what you want built (at least a sentence)."
    if (sanitizedLength(message) > MAX_MESSAGE_LENGTH)
      return `Keep the description under ${MAX_MESSAGE_LENGTH} characters. Save the detail for our reply.`
    return ""
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }

    setError("")
    setState("submitting")

    try {
      const response = await fetch("/api/contact/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          subject: CUSTOM_BUILD_SUBJECT,
          message: message.trim(),
        }),
      })

      const result = await response.json()

      if (response.ok && result.success) {
        setTicketNumber(result.ticket_number || "")
        trackEvent("studio_inquiry_submitted", {
          message_length: message.trim().length,
        })
        setState("done")
      } else {
        throw new Error(result.error || "Submission failed")
      }
    } catch (err: any) {
      setState("idle")
      const msg = String(err?.message || "").toLowerCase()
      if (msg.includes("rate limit") || msg.includes("too many")) {
        setError("Too many requests from this connection. Wait a few minutes and try again.")
      } else if (msg.includes("must be") || msg.includes("invalid")) {
        // Server-side validation message; show it as-is so the reader can fix the field.
        setError(String(err.message))
      } else {
        setError("The inquiry didn't send. Check your connection and try again, or email us through the contact page.")
      }
    }
  }

  if (state === "done") {
    return (
      <div className="st-success" role="status" tabIndex={-1} ref={successRef}>
        <div className="st-success-flag" aria-hidden="true">
          <FlagGlyph />
        </div>
        <p className="st-success-title">Inquiry logged{ticketNumber ? ` as #${ticketNumber}` : ""}.</p>
        <p className="st-success-body">
          Your build request is in our team&apos;s queue, and a confirmation email is
          on its way to you. We read every inquiry ourselves and reply within two
          business days with questions or a scoped quote.
        </p>
        <p className="st-success-next">
          While you wait, the build log above shows the kind of work we&apos;ll bring
          to your project.
        </p>
      </div>
    )
  }

  return (
    <form className="st-form" onSubmit={handleSubmit} noValidate>
      <div className="st-form-row">
        <label className="st-field">
          <span className="st-label">Name</span>
          <input
            className="st-input"
            type="text"
            name="name"
            autoComplete="name"
            value={name}
            maxLength={100}
            required
            aria-required="true"
            aria-invalid={Boolean(error) || undefined}
            aria-describedby={error ? "studio-form-error" : undefined}
            onChange={(e) => setName(e.target.value)}
            disabled={state === "submitting"}
          />
        </label>
        <label className="st-field">
          <span className="st-label">Email</span>
          <input
            className="st-input"
            type="email"
            name="email"
            autoComplete="email"
            inputMode="email"
            value={email}
            maxLength={200}
            required
            aria-required="true"
            aria-invalid={Boolean(error) || undefined}
            aria-describedby={error ? "studio-form-error" : undefined}
            onChange={(e) => setEmail(e.target.value)}
            disabled={state === "submitting"}
          />
        </label>
      </div>
      <label className="st-field">
        <span className="st-label">What do you want built?</span>
        <textarea
          className="st-input st-textarea"
          name="message"
          rows={5}
          value={message}
          maxLength={MAX_MESSAGE_LENGTH}
          required
          aria-required="true"
          aria-invalid={Boolean(error) || undefined}
          aria-describedby={error ? "studio-form-error" : undefined}
          placeholder="Example: a quoting calculator for our sod installation company, branded to us, that emails leads to our office."
          onChange={(e) => setMessage(e.target.value)}
          disabled={state === "submitting"}
        />
      </label>
      {error ? (
        <p className="st-error" id="studio-form-error" role="alert">
          {error}
        </p>
      ) : null}
      <button className="st-cta st-cta-submit st-cta-beam" type="submit" disabled={state === "submitting"}>
        {state === "submitting" ? "Sending your request…" : "Get a build quote"}
      </button>
      <p className="st-form-fineprint">
        No retainer, no sales call required. You get a reply from the team that
        built this site.
      </p>
    </form>
  )
}

/**
 * Sticky bottom quote bar. Appears after the reader scrolls past the hero and
 * hides once the inquiry form is on screen, so the page never shows two
 * competing CTAs at once.
 */
export function StudioStickyBar() {
  const [visible, setVisible] = useState(false)
  const firedRef = useRef(false)
  const barRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const hero = document.getElementById("studio-hero")
    const form = document.getElementById("inquiry")
    if (!hero || !form) return

    let heroGone = false
    let formVisible = false

    const update = () => {
      const next = heroGone && !formVisible
      setVisible(next)
      // Hiding an aria-hidden subtree must not strand keyboard focus inside it.
      if (!next && barRef.current?.contains(document.activeElement)) {
        ;(document.activeElement as HTMLElement).blur()
      }
      if (next && !firedRef.current) {
        firedRef.current = true
        trackEvent("studio_sticky_bar_shown", {})
      }
    }

    const heroObserver = new IntersectionObserver((entries) => {
      heroGone = !entries[0].isIntersecting
      update()
    })
    const formObserver = new IntersectionObserver((entries) => {
      formVisible = entries[0].isIntersecting
      update()
    })

    heroObserver.observe(hero)
    formObserver.observe(form)
    return () => {
      heroObserver.disconnect()
      formObserver.disconnect()
    }
  }, [])

  return (
    <div ref={barRef} className={`st-sticky ${visible ? "st-sticky-visible" : ""}`} aria-hidden={!visible}>
      <span className="st-sticky-copy">Have a build in mind?</span>
      <a
        className="st-cta st-cta-small"
        href="#inquiry"
        tabIndex={visible ? 0 : -1}
        onClick={() => trackEvent("studio_sticky_bar_clicked", {})}
      >
        Get a build quote
      </a>
    </div>
  )
}

function FlagGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M7 21V4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M7 4h10l-2.5 3.5L17 11H7" fill="currentColor" />
    </svg>
  )
}
